import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Inbox, Newspaper, ArrowRight, TrendingUp } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/format';
import { Badge } from '@/components/ui/badge';

function StatCard({ label, value, icon: Icon, accent, sub, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
          <p className="mt-3 font-display text-4xl font-semibold text-[#144726]">{value}</p>
          {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-md ${accent}`}><Icon className="h-5 w-5" /></div>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/stats').then((res) => setData(res.data)).catch(() => {}); }, []);
  if (!data) return <p className="text-slate-500">Loading…</p>;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl font-semibold text-[#144726]">Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">Overview of properties, leads, and editorial activity.</p>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Properties" value={data.properties.total} sub={`${data.properties.available} available • ${data.properties.leased} leased`} icon={Building2} accent="bg-[#C6A35B]/20 text-[#144726]" testId="admin-stat-properties" />
        <StatCard label="Leads" value={data.leads.total} sub={`${data.leads.new} new`} icon={Inbox} accent="bg-[#144726] text-[#C6A35B]" testId="admin-stat-leads" />
        <StatCard label="Blog posts" value={data.blog.published} sub={`${data.blog.drafts} drafts`} icon={Newspaper} accent="bg-slate-100 text-[#144726]" testId="admin-stat-blog" />
        <StatCard label="Occupancy focus" value="95%" sub="target across managed assets" icon={TrendingUp} accent="bg-[#C6A35B] text-[#144726]" testId="admin-stat-occupancy" />
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold text-[#144726]">Recent leads</h2>
            <Link to="/admin/leads" className="inline-flex items-center gap-1 text-sm font-semibold text-[#144726] hover:text-[#C6A35B]">View all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-5 divide-y divide-slate-100">
            {data.recent_leads?.length === 0 && <p className="text-sm text-slate-500">No leads yet.</p>}
            {data.recent_leads?.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-4">
                <div>
                  <p className="font-semibold text-[#144726]">{l.name}</p>
                  <p className="text-xs text-slate-500">{l.email} • {l.phone}</p>
                </div>
                <div className="text-right">
                  <Badge className="rounded-md bg-[#C6A35B] text-[#144726] border-0">{l.status}</Badge>
                  <p className="mt-1 text-xs text-slate-500">{formatDate(l.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="font-display text-xl font-semibold text-[#144726]">Quick actions</h2>
          <div className="mt-5 space-y-3">
            <Link to="/admin/properties" className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 hover:border-[#C6A35B] hover:bg-[#C6A35B]/5 transition-colors"><span className="text-sm font-semibold text-[#144726]">Add a new property</span><ArrowRight className="h-4 w-4 text-[#C6A35B]" /></Link>
            <Link to="/admin/blog" className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 hover:border-[#C6A35B] hover:bg-[#C6A35B]/5 transition-colors"><span className="text-sm font-semibold text-[#144726]">Publish a blog post</span><ArrowRight className="h-4 w-4 text-[#C6A35B]" /></Link>
            <Link to="/admin/leads" className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 hover:border-[#C6A35B] hover:bg-[#C6A35B]/5 transition-colors"><span className="text-sm font-semibold text-[#144726]">Review incoming leads</span><ArrowRight className="h-4 w-4 text-[#C6A35B]" /></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
